import React, { useCallback, useEffect, useState } from "react";
import { Images, Check, X, Loader2, RefreshCw, Car, MessageSquare, Star, Flag, Trash2, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { PageHeader, EmptyState, LoadingBlock } from "../../components/app/ui";
import { adminApi, privateCarApi, errorMessage, formatDate } from "../../lib/api";

const PHOTO_TABS = ["pending", "approved", "rejected"];

export default function PhotoModeration() {
  const [status, setStatus] = useState("pending");
  const [photos, setPhotos] = useState(null);
  const [flagged, setFlagged] = useState(null);
  const [counts, setCounts] = useState({});
  const [flaggedCount, setFlaggedCount] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const isFlagged = status === "flagged";

  const load = useCallback(() => {
    setSelected(new Set());
    if (status === "flagged") {
      setFlagged(null);
      adminApi.flaggedReviews().then((r) => { setFlagged(r.reviews || []); setFlaggedCount(r.count || 0); }).catch((e) => { toast.error(errorMessage(e)); setFlagged([]); });
    } else {
      setPhotos(null);
      adminApi.photoModeration(status).then(setPhotos).catch((e) => { toast.error(errorMessage(e)); setPhotos([]); });
    }
  }, [status]);
  const loadCounts = () => {
    adminApi.photoModerationCounts().then(setCounts).catch(() => setCounts({}));
    adminApi.flaggedReviews().then((r) => setFlaggedCount(r.count || 0)).catch(() => {});
  };
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCounts(); }, []);

  const decide = async (p, approve) => {
    setBusyId(p.photo_id);
    try {
      if (approve) await adminApi.approvePhoto(p.photo_id); else await adminApi.rejectPhoto(p.photo_id);
      toast.success(approve ? "Photo approved" : "Photo removed");
      load(); loadCounts();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusyId(null); }
  };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => (s.size === (photos || []).length ? new Set() : new Set((photos || []).map((p) => p.photo_id))));
  const bulk = async (action) => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const r = await adminApi.bulkModerate([...selected], action);
      toast.success(`${r.processed} photo${r.processed === 1 ? "" : "s"} ${action === "approve" ? "approved" : "removed"}`);
      load(); loadCounts();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBulkBusy(false); }
  };

  const actReview = async (rv, remove) => {
    setBusyId(rv.review_id);
    try {
      if (remove) await adminApi.removeReview(rv.review_id); else await adminApi.dismissFlag(rv.review_id);
      toast.success(remove ? "Review removed" : "Flag dismissed");
      load(); loadCounts();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusyId(null); }
  };

  const Tab = ({ id, label, count, icon: Icon }) => (
    <button onClick={() => setStatus(id)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold capitalize transition-colors ${status === id ? "bg-brand text-white" : "bg-surface-100 text-ink-soft hover:bg-surface-200"}`} data-testid={`photomod-tab-${id}`}>
      {Icon && <Icon className="h-3.5 w-3.5" />}{label}{count != null ? ` (${count})` : ""}
    </button>
  );

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader title="Photo moderation" description="Review private-car photos, customer review photos, and reported reviews before they appear publicly." />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          {PHOTO_TABS.map((t) => <Tab key={t} id={t} label={t} count={counts[t]} />)}
          <Tab id="flagged" label="Flagged reviews" count={flaggedCount} icon={Flag} />
        </div>
        <Button variant="ghost" size="sm" onClick={() => { load(); loadCounts(); }} className="ml-auto" data-testid="photomod-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      {!isFlagged && (photos || []).length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-surface-200 bg-surface-50 px-3 py-2" data-testid="photomod-bulk-bar">
          <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-semibold text-ink-soft">
            <input type="checkbox" checked={selected.size > 0 && selected.size === (photos || []).length} onChange={toggleAll} data-testid="photomod-select-all" className="h-4 w-4 accent-brand" />
            Select all
          </label>
          <span className="text-xs text-ink-muted" data-testid="photomod-selected-count">{selected.size} selected</span>
          <div className="ml-auto flex gap-1.5">
            <Button size="sm" disabled={selected.size === 0 || bulkBusy} onClick={() => bulk("approve")} className="h-8 rounded-full bg-emerald-600 text-xs text-white hover:bg-emerald-700" data-testid="photomod-bulk-approve">
              {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Check className="mr-1 h-3.5 w-3.5" />Approve selected</>}
            </Button>
            <Button size="sm" variant="outline" disabled={selected.size === 0 || bulkBusy} onClick={() => bulk("reject")} className="h-8 rounded-full border-red-300 text-xs text-red-600 hover:bg-red-50" data-testid="photomod-bulk-reject">
              <X className="mr-1 h-3.5 w-3.5" />Remove selected
            </Button>
          </div>
        </div>
      )}

      {isFlagged ? (
        flagged === null ? <LoadingBlock /> : flagged.length === 0 ? (
          <EmptyState icon={Flag} title="No flagged reviews" text="Reviews reported by customers will show here, most-reported first." />
        ) : (
          <div className="space-y-3" data-testid="photomod-flagged-list">
            {flagged.map((rv) => (
              <div key={rv.review_id} className="rounded-2xl border border-amber-200 bg-amber-50/40 p-3" data-testid={`flagged-review-${rv.review_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="flex items-center gap-1.5 text-sm font-semibold text-ink">
                      <span className="inline-flex items-center gap-0.5">{rv.stars}<Star className="h-3 w-3 fill-amber-400 text-amber-400" /></span>
                      <span className="text-ink-soft">· {rv.customer_name || "Customer"} → {rv.driver_name || "driver"}</span>
                    </p>
                    {rv.comment && <p className="mt-1 text-sm text-ink-soft">{rv.comment}</p>}
                    {rv.photo_ids?.length > 0 && (
                      <div className="mt-2 flex gap-1.5 overflow-x-auto">
                        {rv.photo_ids.map((pid) => (<img key={pid} src={privateCarApi.photoUrl(pid)} alt="review" className="h-16 w-16 shrink-0 rounded-lg object-cover" loading="lazy" />))}
                      </div>
                    )}
                    {rv.driver_reply && <p className="mt-1.5 rounded bg-white px-2 py-1 text-xs text-ink-muted"><span className="font-semibold">Driver:</span> {rv.driver_reply}</p>}
                  </div>
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-red-100 px-2 py-1 text-[11px] font-bold text-red-700" data-testid={`flagged-count-${rv.review_id}`}><Flag className="h-3 w-3" />{rv.flag_count}</span>
                </div>
                {rv.flags?.length > 0 && (
                  <div className="mt-2 space-y-0.5 rounded-lg bg-white/70 px-2.5 py-1.5">
                    {rv.flags.map((f, i) => (
                      <p key={i} className="text-[11px] text-ink-muted"><span className="font-semibold text-ink-soft">{f.reporter_name || "A customer"}:</span> {f.reason || "(no reason given)"}</p>
                    ))}
                  </div>
                )}
                <div className="mt-2.5 flex gap-1.5">
                  <Button size="sm" onClick={() => actReview(rv, true)} disabled={busyId === rv.review_id} className="h-8 rounded-full bg-red-600 text-xs text-white hover:bg-red-700" data-testid={`flagged-remove-${rv.review_id}`}>
                    {busyId === rv.review_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Trash2 className="mr-1 h-3.5 w-3.5" />Remove review</>}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => actReview(rv, false)} disabled={busyId === rv.review_id} className="h-8 rounded-full text-xs" data-testid={`flagged-dismiss-${rv.review_id}`}>
                    <ShieldOff className="mr-1 h-3.5 w-3.5" />Dismiss flag
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : photos === null ? (
        <LoadingBlock />
      ) : photos.length === 0 ? (
        <EmptyState icon={Images} title={`No ${status} photos`} text="Nothing here right now." />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4" data-testid="photomod-grid">
          {photos.map((p) => {
            const sel = selected.has(p.photo_id);
            return (
              <div key={p.photo_id} className={`overflow-hidden rounded-2xl border bg-white ${sel ? "border-brand ring-2 ring-brand/30" : "border-surface-300"}`} data-testid={`photomod-card-${p.photo_id}`}>
                <div className="relative aspect-square bg-surface-100">
                  <a href={privateCarApi.photoUrl(p.photo_id)} target="_blank" rel="noreferrer" className="block h-full w-full">
                    <img src={privateCarApi.photoUrl(p.photo_id)} alt="moderation" className="h-full w-full object-cover" loading="lazy" />
                  </a>
                  <label className="absolute left-2 top-2 inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-md bg-white/90 shadow">
                    <input type="checkbox" checked={sel} onChange={() => toggleSel(p.photo_id)} className="h-4 w-4 accent-brand" data-testid={`photomod-select-${p.photo_id}`} />
                  </label>
                </div>
                <div className="space-y-1.5 p-2.5">
                  <p className="flex items-center gap-1 text-[11px] font-semibold text-ink">
                    {p.kind === "review" ? <MessageSquare className="h-3 w-3 text-brand" /> : <Car className="h-3 w-3 text-brand" />}
                    {p.kind === "review" ? "Review photo" : "Car photo"}
                  </p>
                  <p className="truncate text-[11px] text-ink-soft" data-testid={`photomod-driver-${p.photo_id}`}>{p.driver_name || p.driver_email || p.user_id}</p>
                  {p.kind === "review" && (
                    <p className="text-[11px] text-ink-muted">
                      {p.review_stars ? <span className="inline-flex items-center gap-0.5">{p.review_stars}<Star className="h-2.5 w-2.5 fill-amber-400 text-amber-400" /></span> : null}
                      {p.review_comment ? <span className="line-clamp-2"> {p.review_comment}</span> : null}
                    </p>
                  )}
                  <p className="text-[10px] text-ink-muted">{formatDate(p.created_at)}</p>
                  {status === "pending" && (
                    <div className="flex gap-1.5 pt-0.5">
                      <Button size="sm" onClick={() => decide(p, true)} disabled={busyId === p.photo_id} className="h-8 flex-1 rounded-full bg-emerald-600 text-xs text-white hover:bg-emerald-700" data-testid={`photomod-approve-${p.photo_id}`}>
                        {busyId === p.photo_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Check className="mr-1 h-3.5 w-3.5" />Approve</>}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => decide(p, false)} disabled={busyId === p.photo_id} className="h-8 flex-1 rounded-full border-red-300 text-xs text-red-600 hover:bg-red-50" data-testid={`photomod-reject-${p.photo_id}`}>
                        <X className="mr-1 h-3.5 w-3.5" />Remove
                      </Button>
                    </div>
                  )}
                  {status === "approved" && (
                    <Button size="sm" variant="outline" onClick={() => decide(p, false)} disabled={busyId === p.photo_id} className="h-8 w-full rounded-full border-red-300 text-xs text-red-600 hover:bg-red-50" data-testid={`photomod-reject-${p.photo_id}`}>
                      <X className="mr-1 h-3.5 w-3.5" />Remove from public
                    </Button>
                  )}
                  {status === "rejected" && (
                    <Button size="sm" variant="outline" onClick={() => decide(p, true)} disabled={busyId === p.photo_id} className="h-8 w-full rounded-full text-xs" data-testid={`photomod-approve-${p.photo_id}`}>
                      <Check className="mr-1 h-3.5 w-3.5" />Restore &amp; approve
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
